import { supabase } from "@/integrations/supabase/client";

export interface Game {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  thumbnail_url: string | null;
  is_enabled: boolean;
  max_players: number;
  min_players: number;
  xp_reward: number;
  win_xp_bonus: number;
  created_at: string;
}

export interface GameSession {
  id: string;
  game_id: string;
  host_user_id: string | null;
  room_code: string;
  status: 'waiting' | 'in_progress' | 'completed' | 'cancelled';
  started_at: string;
  ended_at: string | null;
  winner_user_id: string | null;
}

export interface GameParticipant {
  id: string;
  session_id: string;
  user_id: string;
  joined_at: string;
  score: number;
  placement: number | null;
}

export const gameService = {
  async getGames(): Promise<Game[]> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('is_enabled', true)
      .order('name');
    
    if (error) throw error;
    return (data || []) as Game[];
  },

  async getAllGames(): Promise<Game[]> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return (data || []) as Game[];
  },

  async getGameBySlug(slug: string): Promise<Game | null> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('slug', slug)
      .single();
    
    if (error) return null;
    return data as Game;
  },

  async createSession(gameId: string, roomCode: string, hostUserId: string): Promise<GameSession> {
    const { data, error } = await supabase
      .from('game_sessions')
      .insert({
        game_id: gameId,
        room_code: roomCode,
        host_user_id: hostUserId,
        status: 'waiting'
      })
      .select()
      .single();
    
    if (error) throw error;
    return data as GameSession;
  },

  async joinSession(sessionId: string, userId: string): Promise<GameParticipant> {
    const { data, error } = await supabase
      .from('game_participants')
      .insert({
        session_id: sessionId,
        user_id: userId
      })
      .select()
      .single();
    
    if (error) throw error;
    return data as GameParticipant;
  },

  async updateSessionStatus(sessionId: string, status: GameSession['status']): Promise<void> {
    const { error } = await supabase
      .from('game_sessions')
      .update({ 
        status,
        ...(status === 'completed' || status === 'cancelled' ? { ended_at: new Date().toISOString() } : {})
      })
      .eq('id', sessionId);
    
    if (error) throw error;
  },

  async getActiveSessions(gameId: string): Promise<GameSession[]> {
    const { data, error } = await supabase
      .from('game_sessions')
      .select('*')
      .eq('game_id', gameId)
      .in('status', ['waiting', 'in_progress'])
      .order('started_at', { ascending: false });
    
    if (error) throw error;
    return (data || []) as GameSession[];
  },

  async toggleGame(gameId: string, isEnabled: boolean): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({ is_enabled: isEnabled })
      .eq('id', gameId);
    
    if (error) throw error;
  },

  async updateGame(gameId: string, updates: Partial<Game>): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update(updates)
      .eq('id', gameId);
    
    if (error) throw error;
  }
};
